import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Twitter, Facebook, Instagram, Youtube, Globe } from 'lucide-react';
import { doc, getDoc, collection, getDocs, query, orderBy, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, isFirebaseConfigured } from '../../firebase/config';
import './Footer.css';

interface Category {
  id: string;
  name: string;
  slug: string;
  description: string;
  postCount?: number;
}

interface SiteSettings {
  siteName: string;
  tagline: string;
  logoUrl: string;
  accentColor: string;
  footerDescription: string;
  socialLinks: {
    name: string;
    url: string;
    iconUrl: string;
  }[];
  affiliateDisclosure: {
    enabled: boolean;
    text: string;
  };
  footerMenu: {
    quickLinks: {
      title: string;
      url: string;
      isActive: boolean;
    }[];
    supportLinks: {
      title: string;
      url: string;
      isActive: boolean;
    }[];
    showCategories: boolean;
    showNewsletter: boolean;
    newsletterTitle: string;
    newsletterDescription: string;
  };
}

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [categories, setCategories] = useState<Category[]>([]);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [newsletterMessage, setNewsletterMessage] = useState<string>('');
  const [siteSettings, setSiteSettings] = useState<SiteSettings>({
    siteName: 'NortiqBlog',
    tagline: 'Your trusted source for honest product reviews',
    logoUrl: '',
    accentColor: '#fa5d5d',
    footerDescription: 'Your trusted source for honest product reviews and recommendations. We help you make informed purchasing decisions with detailed, unbiased reviews.',
    socialLinks: [
      { name: 'Twitter', url: '', iconUrl: '' },
      { name: 'Facebook', url: '', iconUrl: '' },
      { name: 'Instagram', url: '', iconUrl: '' },
      { name: 'YouTube', url: '', iconUrl: '' }
    ],
    affiliateDisclosure: {
      enabled: true,
      text: 'This website contains affiliate links. We may earn a commission when you make a purchase through these links at no additional cost to you. This helps us maintain our independence and continue providing free, high-quality content.'
    },
    footerMenu: {
      quickLinks: [
        { title: 'Home', url: '/', isActive: true },
        { title: 'About Us', url: '/about', isActive: true },
        { title: 'Contact', url: '/contact', isActive: true },
        { title: 'Categories', url: '/categories', isActive: true }
      ],
      supportLinks: [
        { title: 'Privacy Policy', url: '/privacy', isActive: true },
        { title: 'Terms of Service', url: '/terms', isActive: true },
        { title: 'Affiliate Disclosure', url: '/affiliate-disclosure', isActive: true },
        { title: 'Help Center', url: '/contact', isActive: true }
      ],
      showCategories: true,
      showNewsletter: true,
      newsletterTitle: 'Stay Updated',
      newsletterDescription: 'Get the latest product reviews and recommendations delivered to your inbox.'
    }
  });

  const loadCategories = useCallback(async () => {
    try {
      if (!isFirebaseConfigured || !db) return;
      
      const categoriesQuery = query(collection(db, 'categories'), orderBy('name'));
      const categoriesSnapshot = await getDocs(categoriesQuery);
      const categoriesData = categoriesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Category[];
      
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading categories in footer:', error);
    }
  }, []);

  const loadSiteSettings = useCallback(async () => {
    try {
      if (!isFirebaseConfigured || !db) return;
      
      const settingsDoc = await getDoc(doc(db, 'siteSettings', 'main'));
      if (settingsDoc.exists()) {
        const settings = settingsDoc.data();
        setSiteSettings(prev => ({
          ...prev,
          siteName: settings.siteName || prev.siteName,
          tagline: settings.tagline || prev.tagline,
          logoUrl: settings.logoUrl || prev.logoUrl,
          accentColor: settings.accentColor || prev.accentColor,
          footerDescription: settings.footerDescription || prev.footerDescription,
          socialLinks: settings.socialLinks || prev.socialLinks,
          affiliateDisclosure: settings.affiliateDisclosure || prev.affiliateDisclosure,
          footerMenu: settings.footerMenu || prev.footerMenu
        }));
      }
    } catch (error) {
      console.error('Error loading site settings in footer:', error);
    }
  }, []);

  useEffect(() => {
    loadSiteSettings();
    loadCategories();
  }, [loadSiteSettings, loadCategories]); // Footer doesn't need to reload on auth change

  const handleNewsletterSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (newsletterStatus === 'submitting') return;

    const email = newsletterEmail.trim();
    const emailRegex = /[^@\s]+@[^@\s]+\.[^@\s]+/;
    if (!emailRegex.test(email)) {
      setNewsletterStatus('error');
      setNewsletterMessage('Please enter a valid email.');
      return;
    }

    if (!isFirebaseConfigured || !db) {
      setNewsletterStatus('error');
      setNewsletterMessage('Subscription is unavailable right now.');
      return;
    }

    try {
      setNewsletterStatus('submitting');
      await addDoc(collection(db, 'newsletter_subscribers'), {
        email,
        createdAt: serverTimestamp(),
        source: 'footer',
      });
      setNewsletterStatus('success');
      setNewsletterMessage('Thanks! You\'re subscribed.');
      setNewsletterEmail('');
    } catch (err) {
      console.error('Newsletter subscribe error:', err);
      setNewsletterStatus('error');
      setNewsletterMessage('Could not subscribe. Please try again.');
    } finally {
      // Clear message after a short delay
      setTimeout(() => {
        setNewsletterStatus('idle');
        setNewsletterMessage('');
      }, 4000);
    }
  }, [newsletterEmail, newsletterStatus]);

  return (
    <footer className="nb-footer">
      <div className="nb-container">
        <div className="nb-footer-content">
          {/* Brand Section */}
          <div className="nb-footer-section nb-footer-brand">
            <Link to="/" className="nb-footer-logo">
              <img 
                src="/logo.png" 
                alt={siteSettings.siteName}
                className="nb-footer-logo-img"
              />
            </Link>
            <p className="nb-footer-description">
              {siteSettings.footerDescription}
            </p>
            {/* Social Icons */}
            <div className="nb-footer-social">
              <a
                href="https://twitter.com/nortiqca"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Twitter"
                className="nb-social-link"
              >
                <Twitter size={20} />
              </a>
              <a
                href="https://www.facebook.com/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="nb-social-link"
              >
                <Facebook size={20} />
              </a>
              <a
                href="https://www.instagram.com/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="nb-social-link"
              >
                <Instagram size={20} />
              </a>
              <a
                href="https://www.youtube.com/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                className="nb-social-link"
              >
                <Youtube size={20} />
              </a>
              <a
                href="mailto:admin@nortiqblog.com"
                aria-label="Email"
                className="nb-social-link"
              >
                <Mail size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="nb-footer-section">
            <h3 className="nb-footer-title">Quick Links</h3>
            <ul className="nb-footer-links">
              {siteSettings.footerMenu.quickLinks
                .filter(link => link.isActive)
                .map((link, index) => (
                  <li key={index}>
                    <Link to={link.url}>{link.title}</Link>
                  </li>
                ))}
            </ul>
          </div>

          {/* Categories */}
          {siteSettings.footerMenu.showCategories && (
            <div className="nb-footer-section">
              <h3 className="nb-footer-title">Categories</h3>
              <ul className="nb-footer-links">
                {categories.slice(0, 6).map((category) => (
                  <li key={category.id}>
                    <Link to={`/category/${category.slug}`}>{category.name}</Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Legal & Support */}
          <div className="nb-footer-section">
            <h3 className="nb-footer-title">Support</h3>
            <ul className="nb-footer-links">
              {siteSettings.footerMenu.supportLinks
                .filter(link => link.isActive)
                .map((link, index) => (
                  <li key={index}>
                    <Link to={link.url}>{link.title}</Link>
                  </li>
                ))}
            </ul>
          </div>

          {/* Newsletter */}
          {siteSettings.footerMenu.showNewsletter && (
            <div className="nb-footer-section nb-footer-newsletter">
              <h3 className="nb-footer-title">{siteSettings.footerMenu.newsletterTitle}</h3>
              <p className="nb-newsletter-text">
                {siteSettings.footerMenu.newsletterDescription}
              </p>
              <form className="nb-newsletter-form" onSubmit={handleNewsletterSubmit} noValidate>
                <div className="nb-newsletter-input-group">
                  <Mail className="nb-newsletter-icon" size={18} />
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="nb-newsletter-input"
                    required
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    aria-label="Email address"
                  />
                </div>
                <button 
                  type="submit" 
                  className="nb-newsletter-btn"
                  disabled={newsletterStatus === 'submitting'}
                  aria-live="polite"
                >
                  {newsletterStatus === 'submitting' ? 'Subscribing…' : 'Subscribe'}
                </button>
                {newsletterMessage && (
                  <div 
                    className="nb-newsletter-feedback"
                    role={newsletterStatus === 'error' ? 'alert' : 'status'}
                    style={{
                      marginTop: '8px',
                      fontSize: '0.9rem',
                      color: newsletterStatus === 'error' ? '#b71c1c' : 'var(--text-secondary)'
                    }}
                  >
                    {newsletterMessage}
                  </div>
                )}
              </form>
            </div>
          )}
        </div>

        {/* Footer Bottom */}
        <div className="nb-footer-bottom">
          <div className="nb-footer-bottom-content">
            <p className="nb-copyright">
              © {currentYear} {siteSettings.siteName}. All rights reserved.
            </p>
            <p className="nb-footer-note">
              {siteSettings.tagline || 'Made with ❤️ for honest reviews'}
            </p>
          </div>
        </div>

        {/* Affiliate Disclosure removed per request */}
      </div>
    </footer>
  );
};

export default Footer;
